import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  QrCode, Plus, Download, Eye, Trash2, RefreshCw, MapPin,
  Building2, ArrowLeft, Printer, Copy, Check, Search, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LOCATION_TYPES = [
  { value: "zone", label: "Zone", color: "bg-blue-500" },
  { value: "floor", label: "Floor Area", color: "bg-green-500" },
  { value: "washroom", label: "Washroom", color: "bg-purple-500" },
  { value: "parking", label: "Parking", color: "bg-gray-500" },
  { value: "entrance", label: "Entrance", color: "bg-yellow-500" },
  { value: "elevator", label: "Elevator", color: "bg-cyan-500" },
  { value: "escalator", label: "Escalator", color: "bg-pink-500" },
  { value: "food_court", label: "Food Court", color: "bg-orange-500" },
  { value: "common_area", label: "Common Area", color: "bg-teal-500" },
  { value: "emergency_exit", label: "Emergency Exit", color: "bg-red-500" },
];

export default function QRCodeManagementPage() {
  const { mallId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [mall, setMall] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [qrImage, setQrImage] = useState(null);
  const [bulkQRs, setBulkQRs] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    location_name: '', location_type: 'zone', floor: 0, zone: '', description: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token && mallId) {
      fetchData();
    }
  }, [token, mallId]);

  useEffect(() => {
    // Filter locations
    let filtered = [...locations];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(l => 
        l.location_name.toLowerCase().includes(query) ||
        l.qr_code.toLowerCase().includes(query) ||
        l.zone?.toLowerCase().includes(query)
      );
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(l => l.location_type === filterType);
    }
    
    if (filterFloor !== 'all') {
      filtered = filtered.filter(l => l.floor === parseInt(filterFloor));
    }
    
    setFilteredLocations(filtered);
  }, [locations, searchQuery, filterType, filterFloor]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [mallRes, locRes] = await Promise.all([
        axios.get(`${API}/api/malls/${mallId}`, { headers }),
        axios.get(`${API}/api/qr/locations/${mallId}`, { headers })
      ]);
      
      setMall(mallRes.data);
      setLocations(locRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load QR locations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!form.location_name) {
      toast.error("Location name is required");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/qr/locations`, {
        ...form,
        mall_id: mallId
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("QR location created");
      setShowCreateDialog(false);
      setForm({ location_name: '', location_type: 'zone', floor: 0, zone: '', description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create location");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewQR = async (location) => {
    setSelectedLocation(location);
    try {
      const res = await axios.get(`${API}/api/qr/generate-base64/${location.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrImage(res.data.base64_image);
      setShowQRDialog(true);
    } catch (err) {
      toast.error("Failed to generate QR code");
    }
  };

  const handleDownloadQR = async (location) => {
    try {
      const res = await axios.get(`${API}/api/qr/generate/${location.id}?size=400`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${location.qr_code}.png`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("QR code downloaded");
    } catch (err) {
      toast.error("Failed to download QR code");
    }
  };

  const handleGenerateBulk = async () => {
    try {
      const res = await axios.post(`${API}/api/qr/generate-bulk/${mallId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulkQRs(res.data.qr_codes);
      setShowBulkDialog(true);
    } catch (err) {
      toast.error("Failed to generate bulk QR codes");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!confirm("Are you sure you want to deactivate this QR location?")) return;
    
    try {
      await axios.delete(`${API}/api/qr/locations/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Location deactivated");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete location");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const uniqueFloors = [...new Set(locations.map(l => l.floor))].sort((a, b) => a - b);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading QR locations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qr-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/mall/${mallId}/operations`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">QR Code Management</h1>
            <p className="text-muted-foreground">{mall?.name} - Location Tagging</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleGenerateBulk}>
            <Printer className="w-4 h-4 mr-2" />
            Print All QRs
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-location-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Locations</p>
                <p className="text-2xl font-bold">{locations.length}</p>
              </div>
              <QrCode className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Active</p>
                <p className="text-2xl font-bold">{locations.filter(l => l.status === 'active').length}</p>
              </div>
              <Check className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Total Scans</p>
                <p className="text-2xl font-bold">{locations.reduce((sum, l) => sum + (l.scan_count || 0), 0)}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Floors Covered</p>
                <p className="text-2xl font-bold">{uniqueFloors.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, QR code, or zone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Location Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {LOCATION_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterFloor} onValueChange={setFilterFloor}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Floor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Floors</SelectItem>
            {uniqueFloors.map(f => (
              <SelectItem key={f} value={f.toString()}>Floor {f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Locations Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredLocations.map(location => {
          const typeInfo = LOCATION_TYPES.find(t => t.value === location.location_type) || LOCATION_TYPES[0];
          return (
            <Card key={location.id} className="hover:border-primary/50 transition-colors" data-testid={`location-card-${location.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeInfo.color}/20`}>
                      <QrCode className={`w-5 h-5 ${typeInfo.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div>
                      <p className="font-medium">{location.location_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{location.qr_code}</p>
                    </div>
                  </div>
                  <Badge variant={location.status === 'active' ? 'default' : 'secondary'}>
                    {location.status}
                  </Badge>
                </div>
                
                <div className="space-y-1 text-sm mb-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                    <span className="text-muted-foreground">Floor {location.floor}</span>
                  </div>
                  {location.zone && (
                    <p className="text-muted-foreground">Zone: {location.zone}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Scans: {location.scan_count || 0}
                    {location.last_scanned && ` • Last: ${new Date(location.last_scanned).toLocaleDateString()}`}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleViewQR(location)}>
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadQR(location)}>
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(location.qr_code)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => handleDeleteLocation(location.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredLocations.length === 0 && (
        <Card className="p-12 text-center">
          <QrCode className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">No QR locations found</p>
          <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>Create First Location</Button>
        </Card>
      )}

      {/* Create Location Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create QR Location</DialogTitle>
            <DialogDescription>Add a new location to tag with a QR code</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Location Name *</Label>
              <Input
                value={form.location_name}
                onChange={(e) => setForm({...form, location_name: e.target.value})}
                placeholder="e.g., North Wing Washroom, Parking Level B1"
                data-testid="location-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select value={form.location_type} onValueChange={(v) => setForm({...form, location_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Floor *</Label>
                <Input
                  type="number"
                  value={form.floor}
                  onChange={(e) => setForm({...form, floor: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Zone</Label>
              <Input
                value={form.zone}
                onChange={(e) => setForm({...form, zone: e.target.value})}
                placeholder="e.g., North Wing, Central Atrium"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Additional details about this location..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateLocation} disabled={submitting} data-testid="save-location-btn">
              Create Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>{selectedLocation?.location_name}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrImage && (
              <img src={qrImage} alt="QR Code" className="w-48 h-48 border rounded-lg" />
            )}
            <p className="mt-4 font-mono text-sm">{selectedLocation?.qr_code}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {selectedLocation?.location_type} • Floor {selectedLocation?.floor}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => copyToClipboard(selectedLocation?.qr_code)}>
              <Copy className="w-4 h-4 mr-2" />
              Copy Code
            </Button>
            <Button onClick={() => handleDownloadQR(selectedLocation)}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk QR Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All QR Codes ({bulkQRs.length})</DialogTitle>
            <DialogDescription>Print or download all QR codes for this mall</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4" id="bulk-qr-print">
            {bulkQRs.map(qr => (
              <div key={qr.location_id} className="text-center border rounded-lg p-3">
                <img src={qr.base64_image} alt={qr.qr_code} className="w-24 h-24 mx-auto" />
                <p className="font-mono text-xs mt-2">{qr.qr_code}</p>
                <p className="text-xs text-muted-foreground truncate">{qr.location_name}</p>
                <p className="text-xs text-muted-foreground">Floor {qr.floor}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Print All
            </Button>
            <Button onClick={() => setShowBulkDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
